import React from 'react';
import { C } from '../../theme.js';
import { Eyebrow, FieldLabel, TextInput } from '../../components/Primitives.jsx';
import { MultiCols } from '../shared/MultiCols.jsx';
import { OutputNameField } from '../shared/OutputNameField.jsx';
import { SourceSelect } from '../shared/SourceSelect.jsx';
import { sourceColumns } from '../shared/sourceColumns.js';
import { ColumnsContext } from '../StepInspector.jsx';
import { outputNameIssue } from '../../data.js';

// When Source is something other than self, Unpivot restructures a chosen
// Source's own rows, not self's — a different row count/column set than
// the live pipeline payload, unlike Lookup/Arithmetic's per-row additions
// to self. So it can't silently become the new self downstream. Backend
// (executor.py) enforces this: for a non-self Source, next_rows reverts to
// the unchanged self payload after this step, and the reshaped result is
// only reachable through Output Name — required in that case, not optional.

// `id`/`__rowIndex` are bookkeeping keys App.jsx stamps onto every row
// (row-id fallback + original position for React keys/diffing) — never
// real source data. Unlike a bare "id" column elsewhere in this codebase
// (which CAN be genuine business data, so other pickers deliberately don't
// exclude it by name — see feature_engineering's OriginalColumnsContext
// history), these are unconditionally excluded from Unpivot's own column
// pool specifically: melting them produces rows like variable="id",
// value="row_0" that are pure engine metadata. Backend mirrors this
// exclusion (steps/reshape.py's _INTERNAL_COLUMNS) so a stale saved config
// referencing them can't resurrect it either. Scoped to Unpivot only.
const INTERNAL_COLUMNS = new Set(['id', '__rowIndex']);
const withoutInternalColumns = (cols) => (cols || []).filter(c => !INTERNAL_COLUMNS.has(c.name));

export function UnpivotInspector({ step, onChange, pipeline, secondaryDatasets, sheets, onBrowseJoinFile, runMap }) {
  const c = step.config || {};
  const u = (patch) => onChange({ ...step, config: { ...c, ...patch } });

  const sourceIsSelf = !c.source || c.source === '__self__';
  const liveCols = React.useContext(ColumnsContext);
  const sourceCols = sourceColumns(c.source, { sheets, secondaryDatasets, pipeline, runMap });
  const idColumnOptions = withoutInternalColumns(sourceIsSelf ? liveCols : sourceCols);

  const outputNameIssueRes = outputNameIssue(c.output_name, { sheets, secondaryDatasets, steps: pipeline?.steps, stepId: step.id });
  const outputNameMissing = !sourceIsSelf && !(c.output_name || '').trim();

  return (
    <div>
      <Eyebrow>Unpivot</Eyebrow>
      <div style={{ color: C.fg4, fontSize: '.78rem', margin: '4px 0 10px', lineHeight: 1.5 }}>
        Reshape wide columns into long id/variable/value rows.
      </div>

      <div style={{ marginBottom: 10 }}>
        <FieldLabel hint="Dataset to reshape — an uploaded sheet, an earlier published step, or this block's own data">Source</FieldLabel>
        <SourceSelect
          value={c.source}
          onChange={(v) => u({ source: v })}
          secondaryDatasets={secondaryDatasets}
          sheets={sheets}
          pipeline={pipeline}
          currentStep={step}
          onBrowse={onBrowseJoinFile ? () => onBrowseJoinFile(step.id) : undefined}
          buildTargetName={pipeline?.inputFile?.buildTargetName}
        />
      </div>

      <FieldLabel hint="Kept as-is on every output row; every other column in the Source becomes a variable/value pair">ID columns (kept as-is)</FieldLabel>
      <MultiCols columns={idColumnOptions} selected={c.id_vars || []} onChange={(v) => u({ id_vars: v })} />

      <FieldLabel>Variable column name</FieldLabel>
      <TextInput mono value={c.var_name} onChange={(v) => u({ var_name: v })} placeholder="metric" />

      <FieldLabel>Value column name</FieldLabel>
      <TextInput mono value={c.value_name} onChange={(v) => u({ value_name: v })} placeholder="value" />

      <OutputNameField
        value={c.output_name}
        onChange={(v) => u({ output_name: v })}
        required={outputNameMissing}
        hint={sourceIsSelf
          ? 'Optional — also publish this result under a name so later blocks can pick it as their Source'
          : "Required — Source isn't self, so this step's reshaped result doesn't touch the live pipeline payload; without a name it isn't reachable anywhere"}
        warningMessage={<>Source is &ldquo;{c.source}&rdquo;, not self — without an Output Name this step&rsquo;s result goes nowhere: the live pipeline payload stays unchanged and the reshaped rows aren&rsquo;t saved anywhere.</>}
        placeholder="e.g., mas_final"
        issue={outputNameIssueRes}
      />
    </div>
  );
}
